import React from "react";
import { useTranslation } from "react-i18next";

import Modal from "@components/layout/Modal";
import Heading from "@components/layout/Heading";
import ModalInformationIcon from "@components/icons/ModalInformationIcon";
import Label from "@components/form/Label";
import TextInput from "@components/form/TextInput";

import ENV from "@config/env";
import { useUpdateMeter } from "@hooks/mutation/useUpdateMeter";
import { convertLiterToML, convertMLToLiter } from "@utils/convertUnits";
import { toastError, toastSuccess, toastWarning } from "@utils/toast";
import type { Declaration } from "@context/DeclarationContext";
import NoteComposer, {
  DraftNote,
  EMPTY_DRAFT_NOTE,
} from "@components/notes/NoteComposer";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { DBTables } from "@utils/constants/dbTables";

type UpdateMeterClickOverModalProps = {
  open: boolean;
  data: Declaration;
  onSubmit: (clickOver: number) => void;
  onCancel: () => void;
};

const UpdateMeterClickOverModal: React.FunctionComponent<
  UpdateMeterClickOverModalProps
> = ({ open, data, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [clickOver, setClickOver] = React.useState("");
  const [draftNote, setDraftNote] = React.useState<DraftNote>(EMPTY_DRAFT_NOTE);

  const { mutateAsync: updateMeterMutation } = useUpdateMeter();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const newClickOver = convertMLToLiter(+clickOver);
      await updateMeterMutation({
        id: data.meter.id,
        clickOver: newClickOver,
      });

      const noteResult = await flushNoteStepDraft(
        draftNote,
        DBTables.Meters,
        data.meter.id,
      );

      onSubmit(newClickOver);
      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", { artifact: t("common.meter") }),
        );
      } else {
        toastSuccess(t("declaration.toast.click_over.success"));
      }
      setClickOver("");
      setDraftNote(EMPTY_DRAFT_NOTE);
      onCancel();
    } catch (error: any) {
      toastError(
        t("declaration.toast.click_over.failure", {
          error: error?.response?.data?.message || error?.message,
        })
      );
    }
  };

  return (
    <Modal open={open}>
      <form onSubmit={handleSubmit}>
        <div className="py-8 px-4 flex gap-4">
          <ModalInformationIcon className="shrink-0 h-12 w-12" />

          <div className="text-gray-500 space-y-4">
            <Heading className="text-inherit">
              {t("declaration.modal.update_click_over.title")}
            </Heading>

            <div className="max-w-lg">
              <Label htmlFor="current">
                {t("declaration.modal.update_click_over.current_value")}
              </Label>
              <TextInput
                id="current"
                value={convertLiterToML(data.meter.clickOver)}
                suffix={t("common.volume_unit")}
                readOnly
              />
            </div>

            <div className="max-w-lg">
              <Label htmlFor="new">
                {t("declaration.modal.update_click_over.new_value")}
              </Label>
              <TextInput
                id="new"
                type="number"
                value={clickOver}
                onChange={(e) => {
                  setClickOver(e.target.value);
                }}
                placeholder={ENV.DEFAULT_METER_CLICK_OVER}
                suffix={t("common.volume_unit")}
                required
              />
            </div>

            <NoteComposer value={draftNote} onChange={setDraftNote} />
          </div>
        </div>

        <footer className="border-t p-4 flex justify-end gap-4">
          <button
            type="button"
            className="btn-primary text-sm rounded"
            onClick={onCancel}
          >
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-default text-sm rounded">
            {t("common.save")}
          </button>
        </footer>
      </form>
    </Modal>
  );
};

export default UpdateMeterClickOverModal;
